import React, { useEffect, useState } from "react";
import RouteManager from "../../managers/RouteManager";
import { useDispatch, useSelector } from "react-redux";
import {
    BiometricDetailsSelector, fetchBiometricConfig, fetchBiometricDetails, selectBiometricFormField
} from "../../Redux/Slices/BiometricSlice";
import Utility, { deepClone, removeDelayedLoader, showDelayedLoader } from "../../utility/Utility";
import {
    DKInput,
    INPUT_VIEW_DIRECTION,
    DKLabel,
    DKButton,
    DKExpandableWrapper,
    DKLine,
    Toggle,
    showToast,
    TOAST_TYPE,
    showLoader,
    removeLoader,
    DKSpinner
} from "deskera-ui-library";
import { CONSTANTS, ERF, INPUT_TYPE } from "../../constants/Constant";
import EmployeeListService from "../../services/EmployeeListService";
import { scrollToError } from "../../managers/common/CommonUtil";
import MessageInfo from "../common/MessageInfo";
import BiometricAttendanceService from "../../services/BiometricAttendanceService";
import PermissionUtils, { ACTION, MODULE } from "../user-permissions/PermissionUtils";


const BiometricIntegrationSettings = (props) => {

    const fields = [
        {
            columnCode: "activated",
            uiVisible: true,
            name: "Click here to enable/disable Biometric Attendance Integration",
            validation: [{ required: true }],
            type: INPUT_TYPE.TOGGLE,
            options: []
        },
        {
            columnCode: "systemId",
            uiVisible: false,
            name: "Select Biometric system",
            validation: [{ required: true }],
            type: INPUT_TYPE.DROPDOWN,
            options: []
        }];

    /**Hooks */

    const [systemsField, setSystemsField] = useState(fields);
    const [systemFormFields, setSystemFormFields] = useState([]);
    const [mappingFields, setMappingFields] = useState([]);
    const [isProcessing, setProcessing] = useState(false);
    const [formValues, setFromValues] = useState({});
    const [isIntegrationEnabled, setIntegrationFlag] = useState(false);
    const [isSystemIntegrated, setSystemIntegrationFlag] = useState(false);



    /**Selectors */
    const systems = useSelector(selectBiometricFormField);
    const biometricDetail = useSelector(BiometricDetailsSelector);

    /**Dispatcher */
    const dispatch = useDispatch();


    useEffect(() => {
        RouteManager.setPresenter({ props: props });
        setProcessing(true);
        fetchBiometricSystemFieldConfig();
        setProcessing(false);
    }, []);

    useEffect(() => {
        if (!Utility.isEmpty(biometricDetail?.details)) {
            setSystemIntegrationFlag(true);
            setFormValues(biometricDetail?.details);
        } else {
            setSystemIntegrationFlag(false);
        }

    }, [biometricDetail, systems]);

    useEffect(() => {
        if (!Utility.isEmpty(systems)) {
            const fields = deepClone(systemsField);
            const options = updateSystemOptions(systems);
            const alteredConfig = [{ columnCode: "systemId", options: options }];
            EmployeeListService.getAlteredConfigDetails(fields, alteredConfig, false, {}, {});
            setSystemsField(fields);
            fetchActiveBiometricDetails();
        }
    }, [systems]);

    const updateSystemOptions = (systems) => {
        let options = [];
        if (!Utility.isEmpty(systems?.configs) && systems?.configs?.length) {
            options = systems?.configs?.map(m => {
                return {
                    name: m.systemName,
                    id: m.systemId
                }
            })
        }
        return options;
    }

    const setFormValues = (configurationDetails) => {
        const options = updateSystemOptions(systems);
        formValues["activated"] = configurationDetails["activated"];
        formValues["systemId"] = EmployeeListService.getValueObjectFromDropdown(configurationDetails["systemId"], options, "id");
        const systemFields = configurationDetails["systemFields"];
        if(!Utility.isEmptyObject(systemFields)) {
            switch(configurationDetails["systemId"]) {
                case "camAttendance":
                    const kentConfig = systemFields["kentConfig"];
                    for(const k in kentConfig) {
                        switch(kentConfig[k]) {
                            case "mappingFields":
                                const mappingFields = createMappingFields(formValues["systemId"]);
                                kentConfig[k]?.forEach((f) => {
                                    formValues[f["deskeraField"]] = EmployeeListService.getValueObjectFromDropdown(f["systemField"],mappingFields[0].options ,"id");
                                })
                                break;
                            default:
                                formValues[k] = kentConfig[k]
                        }
                    }
                    break;
            }
        }
        if (!Utility.isEmpty(formValues["systemId"])) {
            updateToggleIntegrationSettings("activated");
            createAndEnableFormSections(configurationDetails["systemId"]);
        }
    }

    const updateToggleIntegrationSettings = (c) => {
        const alteredConfig = [];
        const fields = deepClone(systemsField);
        if (formValues[c]) {
            alteredConfig.push({ columnCode: "systemId", uiVisible: true });
            EmployeeListService.getAlteredConfigDetails(fields, alteredConfig, false, {}, {});
        } else {
            alteredConfig.push({ columnCode: "systemId", uiVisible: false });
            EmployeeListService.getAlteredConfigDetails(fields, alteredConfig, false, {}, {});
        }
        setSystemsField(fields);
        setIntegrationFlag(formValues[c]);
    }

    const createSystemFormFields = (systemId) => {
        let sectionFields = [];
        const fields = systems?.configs?.find(f => f.systemId === systemId);
        if (!Utility.isEmpty(fields)) {
            const formFields = fields?.sections?.fields;
            formFields?.forEach(f => {
                let group = {
                    columnCode: f.fieldId,
                    name: f.fieldLabel,
                    type: f.fieldType,
                    ...f
                }
                sectionFields.push(group);
            })
        }
        return sectionFields;
    }

    const createMappingFields = (systemId) => {
        let sectionFields = [];
        const fields = systems?.configs?.find(f => f.systemId === systemId);
        if (!Utility.isEmpty(fields)) {
            const formFields = fields?.sections?.fields;
            if (!Utility.isEmpty(formFields)) {
                const mapperField = formFields.find(f => f.fieldType === "mapper")
                if (!Utility.isEmpty(mapperField) && mapperField?.mappingsSection?.fields) {
                    mapperField?.mappingsSection?.fields?.forEach((f) => {
                        let group = {
                            columnCode: f.fieldId,
                            name: f.fieldLabel,
                            type: f.fieldType,
                            ...f
                        }
                        sectionFields.push(group);
                    })
                }
            }
        }
        return sectionFields;
    }

    const onInputChange = (v, o) => {
        let formFields = deepClone(systemFormFields);
        let systemFieldsCopy = deepClone(systemsField);
        let mappingFieldsCopy = deepClone(mappingFields);
        const alteredConfig = [];
        if (o.columnCode === "systemId") {
            formValues[o.columnCode] = v;
            const value = EmployeeListService.getSelectedIdFromObject(v, "id");
            createAndEnableFormSections(value, o);
            // let { isValid, errMsg } = checkFieldValidation(o);
            // alteredConfig.push({ columnCode: o.columnCode, hasError: !isValid, errMsg: errMsg });
            // EmployeeListService.getAlteredConfigDetails(systemFieldsCopy, alteredConfig, false, {}, {});
            // formFields = createSystemFormFields(value);
            // const mappingFields = createMappingFields(value);
            // setSystemsField(systemFieldsCopy);
            // setSystemFormFields(formFields);
            // setMappingFields(mappingFields);
        } else {
            if (Utility.isEmptyObject(formValues["systemId"])) {
                checkIfValidSystem();
            } else {
                formValues[o.columnCode] = v;
                let { isValid, errMsg } = checkFieldValidation(o);
                alteredConfig.push({ columnCode: o.columnCode, hasError: !isValid, errMsg: errMsg });
                EmployeeListService.getAlteredConfigDetails(formFields, alteredConfig, false, {}, {});
                EmployeeListService.getAlteredConfigDetails(mappingFieldsCopy, alteredConfig, false, {}, {});
                setSystemFormFields(formFields);
                setMappingFields(mappingFieldsCopy);
            }
        }
    }

    const createAndEnableFormSections = (value,o) => {
        let formFields = deepClone(systemFormFields);
        let systemFieldsCopy = deepClone(systemsField);
        const alteredConfig = [];
        if(!Utility.isEmptyObject(o)) {
            let { isValid, errMsg } = checkFieldValidation(o);
            alteredConfig.push({ columnCode: o.columnCode, hasError: !isValid, errMsg: errMsg });
            EmployeeListService.getAlteredConfigDetails(systemFieldsCopy, alteredConfig, false, {}, {});
        }
        formFields = createSystemFormFields(value);
        const mappingFields = createMappingFields(value);
        setSystemsField(systemFieldsCopy);
        setSystemFormFields(formFields);
        setMappingFields(mappingFields);
    }

    const checkIfValidSystem = () => {
        const systemFieldsCopy = deepClone(systemsField);
        const field = systemFieldsCopy[0];
        let { isValid, errMsg } = checkFieldValidation(field);
        const alteredConfig = [];
        alteredConfig.push({ columnCode: field.columnCode, hasError: !isValid, errMsg: errMsg });
        EmployeeListService.getAlteredConfigDetails(systemFieldsCopy, alteredConfig, false, {}, {});
        setSystemsField(systemFieldsCopy);
        setTimeout(() => {
            scrollToError(field?.columnCode);
        }, 100)

    }

    const fetchBiometricSystemFieldConfig = async () => {
        try {
            await dispatch(fetchBiometricConfig())
        } catch (err) {
            setProcessing(false);
        }
    }

    const fetchActiveBiometricDetails = async () => {
        try {
            const timeout = showDelayedLoader();
            await dispatch(fetchBiometricDetails());
            removeDelayedLoader(timeout);
        } catch(err) {
            setProcessing(false);
        }
    }

    const navigateTo = () => {
        RouteManager.navigateToHome();
    }

    const getRequiredFields = (o) => {
        let isRequired = false;
        const validator = o?.validation;
        if (!Utility.isEmpty(validator)) {
            validator?.forEach((f) => {
                for (const k in f) {
                    if (k === "required") {
                        isRequired = f[k];
                    }
                }
            })
        }
        return isRequired;
    }

    const checkFieldValidation = (f) => {
        let isValid = true;
        let errMsg = CONSTANTS.EMPTY_STRING;
        f?.validation?.forEach((v) => {
            for (const k in v) {
                switch (k) {
                    case "required":
                        if (v[k] && Utility.isEmptyObject(formValues[f.columnCode])) {
                            isValid = false;
                            errMsg = `Enter a valid ${f.name}`
                        }
                        break;
                }
            }
        })
        return { isValid, errMsg }
    }

    const getBannerMessageForEmployeeList = () => {
        let messageGroup = [
            {
                message: `Please make sure that mapped fields have same values in both the systems.`,
                isAction: false,
            },
        ];
        return messageGroup;
    }

    const toggleIntegrationSettings = (c) => {
        const alteredConfig = [];
        const fields = deepClone(systemsField)
        formValues[c] = !formValues[c];
        if (formValues[c]) {
            alteredConfig.push({ columnCode: "systemId", uiVisible: true });
            EmployeeListService.getAlteredConfigDetails(fields, alteredConfig, false, {}, {});
        } else {
            alteredConfig.push({ columnCode: "systemId", uiVisible: false });
            EmployeeListService.getAlteredConfigDetails(fields, alteredConfig, false, {}, {});
        }
        setSystemsField(fields);
        setIntegrationFlag(formValues[c]);
    }

    const saveBiometricConfiguration = () => {
        if (isInvalid()) {
            return;
        }
        setProcessing(true);
        const params = getQueryParameters();
        BiometricAttendanceService.saveAttendanceDetails(params).then((response) => {
            if (response) {
                setSystemIntegrationFlag(true);
                setProcessing(false);
                showToast("Biometric settings updated successfully", TOAST_TYPE.SUCCESS);
            }
        }, (error) => {
            setSystemIntegrationFlag(false);
            setProcessing(false);
        })

        console.log(params);
        return;

        BiometricAttendanceService.saveAttendanceDetails((response) => {
            console.log(response);
        })
    }

    const getQueryParameters = () => {
        const systemConfig = {};
        const mappingConfig = [];
        const fieldsConfig = {};

        systemsField.forEach((f) => {
            systemConfig[f.columnCode] = getFieldValue(f);
        });
        mappingFields.forEach((m) => {
            let group = {};
            group["deskeraField"] = m.columnCode;
            group["systemField"] = getFieldValue(m);
            mappingConfig.push(group);
        });
        systemFormFields.forEach((s) => {
            fieldsConfig[s.columnCode] = getFieldValue(s);
        });
        fieldsConfig["mappingFields"] = mappingConfig;

        const systemField = {};
        switch (EmployeeListService.getSelectedIdFromObject(formValues["systemId"], "id")) {
            case "camAttendance":
                systemField["kentConfig"] = fieldsConfig;
        }
        const params = {
            ...systemConfig,
            "systemFields": systemField,
            "time_zone":Intl.DateTimeFormat().resolvedOptions().timeZone
        }

        return params;

    }

    const getFieldValue = (o) => {
        switch (o.type) {
            case INPUT_TYPE.DROPDOWN:
                return EmployeeListService.getSelectedIdFromObject(formValues[o.columnCode], "id");
                break;
            default:
                return formValues[o.columnCode]
        }
    }

    const isInvalid = () => {
        let invalid = false;
        const alteredConfig = [];
        const systemFieldCopy = deepClone(systemsField);
        const systemFormFieldsCopy = deepClone(systemFormFields);
        const mapppingFieldsCopy = deepClone(mappingFields);
        const fields = systemFieldCopy.concat(systemFormFieldsCopy, mapppingFieldsCopy);
        for (let f = 0; f < fields?.length; f++) {
            if (fields[f].columnCode === "activated") {
                const isActive = formValues[fields[f].columnCode];
                if (!isActive) {
                    break;
                }
            } else {
                let { isValid, errMsg } = checkFieldValidation(fields[f]);
                if (!isValid) {
                    invalid = !isValid;
                    alteredConfig.push({ columnCode: fields[f].columnCode, hasError: !isValid, errMsg: errMsg });
                    EmployeeListService.getAlteredConfigDetails(systemFieldCopy, alteredConfig, false, {}, {});
                    EmployeeListService.getAlteredConfigDetails(systemFormFieldsCopy, alteredConfig, false, {}, {});
                    EmployeeListService.getAlteredConfigDetails(mapppingFieldsCopy, alteredConfig, false, {}, {});
                    setSystemsField(systemFieldCopy);
                    setSystemFormFields(systemFormFieldsCopy);
                    setMappingFields(mapppingFieldsCopy);
                }
            }
        }
        if (!Utility.isEmpty(alteredConfig.length)) {
            setTimeout(() => {
                scrollToError(alteredConfig[0].columnCode);
            }, 100)
        }

        return alteredConfig.length > 0;
    }

    const deleteActiveConfiguration = () => {
        showLoader("Delete in progress...");
        
        BiometricAttendanceService.deleteBiometricDetails().then(res=>{
            removeLoader();
            showToast("Biometric settings deleted successfully",TOAST_TYPE.SUCCESS);
            setFromValues({})
            
            setSystemIntegrationFlag(false)
            const alteredConfig = [];
            const fields = deepClone(systemsField);
            alteredConfig.push({ columnCode: "systemId", uiVisible: false });
            EmployeeListService.getAlteredConfigDetails(fields, alteredConfig, false, {}, {});
            setSystemsField(fields);
        },error=>{
            removeLoader();
        })
        
    }

    const getFormFields = (formFields) => {
        return formFields.map((o, i) => {
            return (
                <>
                    {(o.type === INPUT_TYPE.TEXT || o.type === INPUT_TYPE.NUMBER) && o.uiVisible && (
                        <div className={`column p-h-l mb-l mt-s web-width-50 padding-bottom-mobile ${o.columnCode}`}
                            key={o.columnCode}>
                            <DKInput
                                ref={ERF.ADD_EMPLOYEE[o.columnCode]}
                                title={o.name}
                                placeholder={o.name}
                                value={formValues[o.columnCode]}
                                options={o.options}
                                type={o.type}
                                labelClassName=" text-align-left "
                                onChange={(v) => {
                                    onInputChange(v, o);
                                }}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                required={getRequiredFields(o)}
                                readOnly={o.readOnly}
                                canValidate={o.hasError}
                                validator={(v) => {
                                    return !o.hasError;
                                }}
                                errorMessage={o.errMsg}
                                tooltip={{
                                    content: o.tooltipText,
                                    className: "warning-tooltip-prop",
                                }}
                            />
                        </div>

                    )}
                    {o.type === INPUT_TYPE.DROPDOWN && o.uiVisible && (
                        <div className={`column p-h-l mb-xl web-width-50 padding-bottom-mobile ${o.columnCode}`}
                            key={o.columnCode}>
                            <DKInput
                                ref={ERF.ADD_EMPLOYEE[o.columnCode]}
                                title={o.name}
                                placeholder={o.name}
                                value={formValues[o.columnCode]}
                                formatter={(o) => {
                                    /** How to render a selected option in case of object **/
                                    return o.name;
                                }}
                                type={o.type}
                                labelClassName=" text-align-left "
                                onChange={(v) => {
                                    onInputChange(v, o);
                                }}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                required={getRequiredFields(o)}
                                readOnly={o?.readOnly || isSystemIntegrated || isProcessing}
                                dropdownConfig={{
                                    className: "",
                                    style: {},
                                    allowSearch: o.options && o.options.length > 6 ? true : false,
                                    searchableKey: "name",
                                    data: o.options,
                                    renderer: (index, obj) => {
                                        /** How to render value in Dropdown in case of object **/
                                        return <DKLabel text={`${obj.name}`} />;
                                    },
                                    onSelect: (index, obj) => {
                                        // onValueChange(obj, "Quarter");
                                    },
                                }}
                                canValidate={o.hasError}
                                validator={(v) => {
                                    return !o.hasError;
                                }}
                                errorMessage={o.errMsg}
                            />
                        </div>
                    )}
                    {o.type === INPUT_TYPE.TOGGLE && (
                        <div className="p-l">
                            <div className="row" style={{ pointerEvents: isSystemIntegrated || isProcessing ? "none" : "" }}>
                                <Toggle className="mr-s border-n" isOn={formValues[o.columnCode]} onChange={() => toggleIntegrationSettings(o.columnCode)} />
                                <span className="mb-xs">{o.name}</span>
                            </div>
                        </div>
                    )}
                </>
            )
        })
    }

    const getSystemsFieldSection = () => {
        return (
            <>
                <div className="parent-width bg-white border-radius-s card-shadow" style={{ pointerEvents: !PermissionUtils.hasAccess(MODULE.BIO_ATTENDANCE_SETTING, ACTION.CREATE) ? "none" : "" }}>
                    <div className=" parent-width mb-s pt-s">
                        {systemsField && getFormFields(systemsField)}
                    </div>

                </div>
            </>
        )
    }

    const getHeader = () => {
        return (
            <div className="parent-width d-flex justify-content-between mb-l">
                <div className="row">
                    <DKLabel
                        text="Biometric Attendance Settings"
                        className=" fs-r text-align-left fw-m fs-l"
                    />
                    {isProcessing && <DKSpinner className="pl-s" iconClassName="ic-s-2" />}
                </div>

                <div className="d-flex">
                    <DKButton
                        title="Cancel"
                        className="border-radius-m bg-gray1 border-m mr-m"
                        onClick={() => {
                            navigateTo()
                        }}
                    />
                    {PermissionUtils.hasAccess(MODULE.BIO_ATTENDANCE_SETTING, ACTION.CREATE) &&
                        <DKButton
                            title="Save"
                            className="bg-app text-white"
                            onClick={() => {
                                saveBiometricConfiguration()
                            }}
                        />
                    }
                </div>
            </div>
        )
    }

    const getMappingSectionFormFields = (formFields) => {
        return formFields.map((o, i) => {
            return (
                <>
                    {o.type === INPUT_TYPE.DROPDOWN && o.uiVisible && (
                        <div className={`column p-h-l mb-xl web-width-50 padding-bottom-mobile ${o.columnCode}`}
                            key={o.columnCode}>
                            <DKInput
                                ref={ERF.ADD_EMPLOYEE[o.columnCode]}
                                title={o.name}
                                placeholder={o.name}
                                value={formValues[o.columnCode]}
                                formatter={(o) => {
                                    /** How to render a selected option in case of object **/
                                    return o.name;
                                }}
                                type={o.type}
                                labelClassName=" text-align-left "
                                onChange={(v) => {
                                    onInputChange(v, o);
                                }}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                required={getRequiredFields(o)}
                                readOnly={!o.editable || isSystemIntegrated || isProcessing}
                                dropdownConfig={{
                                    className: "",
                                    style: {},
                                    allowSearch: o.options && o.options.length > 6 ? true : false,
                                    searchableKey: "name",
                                    data: o.options,
                                    renderer: (index, obj) => {
                                        /** How to render value in Dropdown in case of object **/
                                        return <DKLabel text={`${obj.name}`} />;
                                    },
                                    onSelect: (index, obj) => {
                                        // onValueChange(obj, "Quarter");
                                    },
                                }}
                                canValidate={o.hasError}
                                validator={(v) => {
                                    return !o.hasError;
                                }}
                                errorMessage={o.errMsg}
                            />
                        </div>
                    )}
                </>
            )
        })
    }

    const getMappingSections = (mappingSection) => {
        return (
            <>
                <div className="mt-m m-h-xxl parent-width bg-white border-radius-s card-shadow-form">
                    <DKExpandableWrapper
                        title={mappingSection?.fieldLabel}
                        className="collapsable"
                        expanded={true}
                        headerStyle={{ padding: "20px" }}
                    >

                        <DKLine className="bg-line-2" />
                        <div className="row p-h-l pt-l">
                            <MessageInfo
                                message={getBannerMessageForEmployeeList()}
                                bannerClass="mb-l"
                                isClosable={false}
                            />
                        </div>
                        <div className="d-flex web-width-50 p-h-l p-v-l fs-m">
                            <div className="" style={{ width: "40%" }}>
                                {"Deskera Fields"}
                            </div>
                            <div className="ml-r">
                                {`${!Utility.isEmpty(formValues["systemId"]) ? EmployeeListService.getSelectedIdFromObject(formValues["systemId"], "name") : ""} System Fields`}
                            </div>
                        </div>
                        <DKLine className="bg-line-2 ml-s mb-m" style={{ width: "50%" }} />
                        <div
                            className=" parent-width pb-s pt-s"
                        >
                            {mappingFields && getMappingSectionFormFields(mappingFields)}
                        </div>
                    </DKExpandableWrapper>
                </div>
            </>
        )
    }

    const getSectionFormFields = (formFields) => {
        return formFields.map((o, i) => {
            return (
                <>
                    {(o.type === INPUT_TYPE.TEXT || o.type === INPUT_TYPE.NUMBER) && o.uiVisible && (
                        <div className={`column p-h-l mb-l web-width-50 padding-bottom-mobile ${o.columnCode}`}
                            key={o.columnCode}>
                            <DKInput
                                ref={ERF.ADD_EMPLOYEE[o.columnCode]}
                                title={o.name}
                                placeholder={o.name}
                                value={formValues[o.columnCode]}
                                options={o.options}
                                type={o.type}
                                labelClassName=" text-align-left "
                                onChange={(v) => {
                                    onInputChange(v, o);
                                }}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                required={getRequiredFields(o)}
                                readOnly={!o.editable || isSystemIntegrated || isProcessing}
                                canValidate={o.hasError}
                                validator={(v) => {
                                    return !o.hasError;
                                }}
                                errorMessage={o.errMsg}
                                tooltip={{
                                    content: o.tooltipText,
                                    className: "warning-tooltip-prop",
                                }}
                            />
                            {/* {o.hasError && <DKLabel
                                className="mt-s pl-s unselectable text-red"
                                style={{
                                    whiteSpace: "nowrap",
                                    fontSize: "11px"
                                }}
                                text={o.errMsg}
                            />} */}
                        </div>

                    )}
                    {o.type === "mapper" && !Utility.isEmpty(mappingFields) && getMappingSections(o)}
                </>
            )
        })
    }

    const getSelectedSystemFields = () => {
        return (
            <>
                <div className="mt-m parent-width bg-white border-radius-s card-shadow">
                    <DKExpandableWrapper
                        title={`${!Utility.isEmpty(formValues["systemId"]) ? EmployeeListService.getSelectedIdFromObject(formValues["systemId"], "name") : ""} System configurations`}
                        className="collapsable"
                        expanded={true}
                        headerStyle={{ padding: "20px" }}
                    >
                        <DKLine className="bg-line-2" />
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                            }}
                            className=" parent-width mb-s pb-m pt-s"
                        >
                            {systemFormFields && getSectionFormFields(systemFormFields)}
                        </div>
                    </DKExpandableWrapper>
                </div>
            </>
        )

    }

    const getConfiguredSystemSection = () => {
        return (
            <>
                <div className="parent-width mb-m">
                    <div className="parent-width d-flex justify-content-between mb-l">
                        <DKLabel
                            text="Biometric Attendance Settings"
                            className=" fs-r text-align-left fw-m fs-l"
                        />
                        {isProcessing && <DKSpinner className="pl-s" iconClassName="ic-s-2" />}
                    </div>
                    <div className="parent-width bg-white border-radius-s card-shadow">
                        <div className="p-h-l p-v-l">
                            <div className="fs-l">
                                <span>
                                    {"Biometric attendance is currently configured with "}
                                </span>
                                <span className="text-app fw-m">
                                    {`${!Utility.isEmpty(formValues["systemId"]) ? EmployeeListService.getSelectedIdFromObject(formValues["systemId"], "name") : ""}`}
                                </span>

                            </div>
                            {PermissionUtils.hasAccess(MODULE.BIO_ATTENDANCE_SETTING, ACTION.DELETE) &&
                                <div className="mt-s row">
                                    <div className="pr-xs" onClick={() => deleteActiveConfiguration()}>
                                        <span className="text-app cursor-hand"><u>{"Click here"}</u></span>
                                    </div>
                                    <span>{" to delete current configuration and manage further integration settings!!"}</span>
                                </div>
                            }
                        </div>
                    </div>
                </div>

            </>

        )
    }




    return (
        <>
            <div className="column parent-width">
                {/* /If integration is enabled and backend gives data */}
                {isSystemIntegrated && getConfiguredSystemSection()}
                {/**When inetgration no enabled i.e backend does not give data */}
                {!isSystemIntegrated && getHeader()}

                {!isSystemIntegrated && getSystemsFieldSection()}
                {isIntegrationEnabled && !Utility.isEmpty(formValues["systemId"]) && getSelectedSystemFields()}
            </div>
        </>
    )

}

export default BiometricIntegrationSettings;